import { Button, Input, InputError, InputWrapperAuth } from '@/components';
import {
	maskCPF,
	maskDate,
	maskPhone,
	normalizeName,
	removeMask,
	transformBirthday,
} from '@/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import {
	ArrowRightCircle,
	Lock,
	IdCard,
	CalendarDays,
	Phone,
} from 'lucide-react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

interface GoogleRegisterProps {
	handleOnRegisterComplement: (data: Users.CreateComplement) => void;
	userId: string;
}

const GoogleRegister = ({
	handleOnRegisterComplement,
	userId,
}: GoogleRegisterProps) => {
	const iconCommonClass = 'w-4 h-4 text-zinc-500';

	const schema = z
		.object({
			id: z.string(),
			password: z
				.string()
				.min(6, 'A senha deve ter no mínimo 6 caracteres')
				.max(255, 'A senha deve ter no máximo 255 caracteres')
				.regex(
					/^[0-9A-Za-zÀ-ÖØ-öø-ÿ!@#$%^&*()_+\-= [\]{};':"\\|,.<>/?~`]+$/,
					'A senha contém caracteres inválidos',
				)
				.regex(
					/^(?=.*[A-Za-z])(?=.*\d).+$/,
					'A senha deve conter pelo menos uma letra e um número',
				),
			repeat_password: z.string(),
			first_name: z
				.string()
				.regex(
					/^[A-Za-zÀ-ÖØ-öø-ÿ\s]+$/,
					'Nome inválido, apenas letras são permitidas',
				)
				.transform(normalizeName)
				.refine(
					(val: string) => val.length >= 3,
					'Mínimo 3 caracteres',
				),
			last_name: z
				.string()
				.regex(
					/^[A-Za-zÀ-ÖØ-öø-ÿ\s]+$/,
					'Sobrenome inválido, apenas letras são permitidas',
				)
				.transform(normalizeName)
				.refine(
					(val: string) => val.length >= 3,
					'Mínimo 3 caracteres',
				),
			birthday: z
				.string()
				.regex(
					/^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/(19\d{2}|20[01]\d|202[0-9])$/,
					'Data inválida (dd/mm/aaaa)',
				)
				.transform(transformBirthday),
			phone_number: z
				.string()
				.min(1, 'Telefone é obrigatório')
				.transform(removeMask)
				.refine(
					(val) => val.length === 11,
					'Número de telefone deve ter 11 dígitos',
				)
				.refine(
					(val) => /^\d+$/.test(val),
					'Número de telefone deve conter apenas números',
				),
			cpf: z
				.string()
				.transform(removeMask)
				.refine(
					(val) => !val || val.length === 11,
					'CPF deve ter 11 dígitos',
				)
				.refine(
					(val) => !val || /^\d+$/.test(val),
					'CPF deve conter apenas números',
				),
			terms_accepted: z.literal(true, {
				errorMap: () => ({
					message: 'Você deve aceitar os termos de uso',
				}),
			}),
		})
		.refine((data) => data.password === data.repeat_password, {
			message: 'As senhas não coincidem',
			path: ['repeat_password'],
		});

	type FormData = z.infer<typeof schema>;

	const {
		register,
		watch,
		handleSubmit,
		setValue,
		formState: { errors },
	} = useForm<FormData>({
		defaultValues: {
			id: userId,
		},
		resolver: zodResolver(schema),
	});

	const onSubmit = (data: FormData) => {
		const { repeat_password, terms_accepted, ...body } = data;
		handleOnRegisterComplement(body);
	};

	return (
		<form
			onSubmit={handleSubmit(onSubmit)}
			className="flex h-full w-full flex-col items-center space-y-5 overflow-hidden overflow-y-auto md:max-w-none md:justify-center"
		>
			<div className="text-center">
				<h3 className="text-2xl font-semibold leading-8">
					Quase lá...
				</h3>
				<p className="text-md text-dark">
					Só precisamos de algumas informações
				</p>
			</div>

			<div className="mx-auto flex w-full max-w-96 flex-col gap-4">
				<div>
					<InputWrapperAuth
						label="Nome"
						icon={<IdCard className={iconCommonClass} />}
						displayError
					>
						<Input
							{...register('first_name')}
							value={watch('first_name') || ''}
							onChange={(e) =>
								setValue(
									'first_name',
									e.target.value.replace(/\s+/g, ' '),
									{
										shouldValidate: false,
									},
								)
							}
							placeholder="Digite seu nome"
						/>
					</InputWrapperAuth>
				</div>
				<div>
					<InputWrapperAuth
						label="Sobrenome"
						icon={<IdCard className={iconCommonClass} />}
						displayError
					>
						<Input
							{...register('last_name')}
							value={watch('last_name') || ''}
							onChange={(e) =>
								setValue(
									'last_name',
									e.target.value.replace(/\s+/g, ' '),
									{
										shouldValidate: false,
									},
								)
							}
							placeholder="Digite seu sobrenome"
						/>
					</InputWrapperAuth>
				</div>
				<div>
					<InputWrapperAuth
						label="Senha"
						icon={<Lock className={iconCommonClass} />}
						displayError
					>
						<Input
							type="password"
							placeholder="********"
							{...register('password')}
						/>
					</InputWrapperAuth>
				</div>
				<div>
					<InputWrapperAuth
						label="Repita a senha"
						icon={<Lock className={iconCommonClass} />}
						displayError
					>
						<Input
							type="password"
							placeholder="********"
							{...register('repeat_password')}
						/>
					</InputWrapperAuth>
				</div>
				<div>
					<InputWrapperAuth
						label="Data de nascimento"
						icon={<CalendarDays className={iconCommonClass} />}
						error={errors.birthday?.message}
						value={watch('birthday')}
						displayError
					>
						<Input
							value={maskDate(watch('birthday') || '')}
							{...register('birthday')}
							onChange={(e) =>
								setValue('birthday', maskDate(e.target.value), {
									shouldValidate: true,
								})
							}
							placeholder="dd/mm/aaaa"
							maxLength={10}
						/>
					</InputWrapperAuth>
				</div>
				<div>
					<InputWrapperAuth
						label="CPF"
						icon={<IdCard className={iconCommonClass} />}
						error={errors.cpf?.message}
						value={watch('cpf')}
						displayError
					>
						<Input
							value={maskCPF(watch('cpf') || '')}
							{...register('cpf')}
							onChange={(e) =>
								setValue('cpf', maskCPF(e.target.value), {
									shouldValidate: true,
								})
							}
							placeholder="000.000.000-00"
							maxLength={14}
						/>
					</InputWrapperAuth>
				</div>
				<div>
					<InputWrapperAuth
						label="Telefone"
						icon={<Phone className={iconCommonClass} />}
						error={errors.phone_number?.message}
						value={watch('phone_number')}
						displayError
					>
						<Input
							maxLength={15}
							value={maskPhone(watch('phone_number') || '')}
							{...register('phone_number')}
							onChange={(e) =>
								setValue(
									'phone_number',
									maskPhone(e.target.value),
									{
										shouldValidate: true,
									},
								)
							}
							placeholder="(00) 00000-0000"
						/>
					</InputWrapperAuth>
				</div>
			</div>
			<div className="relative mx-auto w-full max-w-96">
				<div className="w-full space-y-2">
					<div className="flex flex-wrap items-baseline gap-x-1.5">
						<p className="text-sm text-zinc-700">
							Leia aqui os nossos
						</p>
						<button
							type="button"
							className="text-sm text-primary hover:underline"
						>
							termos de uso
						</button>
						<p className="text-sm text-zinc-700">e</p>
						<button
							type="button"
							className="text-sm text-primary hover:underline"
						>
							política de privacidade.
						</button>
					</div>

					<div className="flex items-center gap-2">
						<input
							type="checkbox"
							id="terms_accepted"
							className="h-4 w-4 cursor-pointer focus:outline-none"
							{...register('terms_accepted')}
						/>
						<label
							htmlFor="terms_accepted"
							className="text-sm leading-3 text-zinc-700"
						>
							Confirmo que li e aceito os
							<strong> termos de uso </strong>e
							<strong> política de privacidade</strong>
						</label>
					</div>
					{errors.terms_accepted && (
						<InputError
							error={errors.terms_accepted.message || ''}
						/>
					)}
				</div>
			</div>
			<Button
				className="mx-auto w-full justify-center rounded-lg py-2 md:max-w-80"
				text="Criar Conta"
				endIcon={
					<ArrowRightCircle className="h-fit w-4 text-light transition-all ease-in group-hover:translate-x-1" />
				}
			/>
		</form>
	);
};

export { GoogleRegister };
